// src/modules/appointments/pages/AppointmentAvailabilityPage.jsx
import { useEffect, useMemo, useState } from "react";
import { createAppointment, getAppointmentsConfig, getAvailability } from "../api/appointmentsApi";
import { useAuth } from "../../../auth/AuthContext";
import AvailabilityViewer from "../components/AvailabilityViewer";
import AppointmentFormModal from "../components/AppointmentFormModal";

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

export default function AppointmentAvailabilityPage() {
  const { user } = useAuth();
  const [resourceId, setResourceId] = useState("");
  const [date, setDate] = useState(todayInputValue());
  const [slots, setSlots] = useState([]);
  const [config, setConfig] = useState({
    services: [],
    resources: [],
    locations: [],
    reasons: [],
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInitialData, setDrawerInitialData] = useState(null);

  useEffect(() => {
    getAppointmentsConfig()
      .then((data) => {
        setConfig({
          services: Array.isArray(data?.services) ? data.services : [],
          resources: Array.isArray(data?.resources) ? data.resources : [],
          locations: Array.isArray(data?.locations) ? data.locations : [],
          reasons: Array.isArray(data?.reasons) ? data.reasons : [],
        });
      })
      .catch((e) => setError(e.message || "No se pudo cargar la configuración."));
  }, []);

  const selectedResource = useMemo(
    () => config.resources.find((r) => Number(r.id) === Number(resourceId)),
    [config.resources, resourceId]
  );

  async function load() {
    try {
      setLoading(true);
      setError("");
      const data = await getAvailability({ resource_id: resourceId, date });
      setSlots(data);
    } catch (e) {
      setError(e.message || "No se pudo cargar la disponibilidad.");
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }

  function openCreateFromSlot(slot) {
    setDrawerInitialData({
      start_at: slot.start,
      end_at: slot.end,
      resource_id: resourceId,
      location_id: selectedResource?.location_id || "",
    });
    setDrawerOpen(true);
  }

  async function handleCreateAppointment(formData) {
    try {
      setSubmitting(true);
      setError("");
      const resource = formData.resource_id || resourceId;
      await createAppointment({
        ...formData,
        resource_id: undefined,
        resources: resource ? [{ resource_id: resource, role: "principal" }] : [],
        created_by_user_id: user?.id ?? null,
        actor_user_id: user?.id ?? null,
      });
      setDrawerOpen(false);
      setDrawerInitialData(null);
      await load();
    } catch (e) {
      setError(e.message || "No se pudo crear la cita.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="appointments-module p-4 max-w-3xl mx-auto space-y-4">
      {/* Header */}
      <div>
        <div className="page-title">Disponibilidad por recurso</div>
        <p className="text-xs text-[color:var(--muted)]">
          Consulta los slots disponibles para un profesional, sala o equipo en una fecha específica.
        </p>
      </div>

      {/* Formulario */}
      <section className="card section space-y-3">
        <h3 className="m-0 text-sm font-semibold">Buscar disponibilidad</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[240px]">
            <label className="block text-[10px] font-semibold uppercase mb-1">
              Médico / recurso
            </label>
            <select
              value={resourceId}
              onChange={(e) => setResourceId(e.target.value)}
              className="input w-full"
            >
              <option value="">Selecciona un médico</option>
              {config.resources.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase mb-1">
              Fecha
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input"
            />
          </div>
          <button
            onClick={load}
            disabled={!resourceId || !date || loading}
            className="btn disabled:opacity-60"
          >
            {loading ? "Buscando..." : "Ver slots"}
          </button>
        </div>

        {error && (
          <div className="text-xs text-red-600 border border-red-300 rounded p-2">
            {error}
          </div>
        )}
      </section>

      {/* Resultado */}
      <section className="card section">
        <h3 className="m-0 mb-2 text-sm font-semibold">Resultados</h3>
        <AvailabilityViewer
          slots={slots}
          loading={loading}
          onPick={openCreateFromSlot}
        />
        {!loading && !error && slots?.length === 0 && (
          <div className="mt-2 text-xs text-[color:var(--muted)]">
            No hay disponibilidad para los parámetros seleccionados.
          </div>
        )}
      </section>

      <AppointmentFormModal
        open={drawerOpen}
        mode="create"
        initialData={drawerInitialData}
        config={config}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleCreateAppointment}
        submitting={submitting}
      />
    </div>
  );
}
